import type { VStackProps } from '@frontend/legacy-components';

import {
  DisplayWrapper,
  DisplayEntry,
  DisplayEntryWrapper,
} from 'modules/common/viewer/components/ViewerDisplayElements';

export type DxfDetailsOverlayProps = {
  /** Overall drawing size, already formatted for display. */
  size: string;
  /** Whether a selection (or hovered layer) contributes detail rows. */
  hasDetail: boolean;
  /** Label for the count row — "Selection" or the hovered layer's name. */
  heading: string;
  /** Number of picked (or hovered-layer) items. */
  count: number;
  /** Total path length, already formatted. */
  length: string;
  /**
   * Enclosed area when a closed loop contributes one; `null` when an open chain
   * or edge chunk encloses nothing — a zero there would be a lie.
   */
  area: string | null;
} & VStackProps;

/**
 * Bottom-right readout of what the drawing is and what is picked.
 *
 * Pure and controlled: always reports the part's overall size; adds the
 * selection's item count and total path length when something is picked, and
 * its enclosed area only when provided. Kept free of the session so it stays
 * trivially testable — `useDxfDetailsOverlay` is the piece that subscribes.
 */
export const DxfDetailsOverlay = ({
  size,
  hasDetail,
  heading,
  count,
  length,
  area,
  ...props
}: DxfDetailsOverlayProps) => (
  <DisplayWrapper
    color="white"
    pointerEvents="none"
    /* Belt and braces: `pointerEvents: none` already means a press falls
       through to the render surface, but stating this makes the intent
       independent of that. */
    userSelect="none"
    data-testid="dxf-details-overlay"
    {...props}
  >
    <DisplayEntryWrapper>
      <DisplayEntry>Size:</DisplayEntry>
      <DisplayEntry>{size}</DisplayEntry>
    </DisplayEntryWrapper>
    {hasDetail && (
      <>
        <DisplayEntryWrapper>
          <DisplayEntry>{heading}:</DisplayEntry>
          <DisplayEntry>{`${count}`}</DisplayEntry>
        </DisplayEntryWrapper>
        <DisplayEntryWrapper>
          <DisplayEntry>Length:</DisplayEntry>
          <DisplayEntry>{length}</DisplayEntry>
        </DisplayEntryWrapper>
        {area !== null && (
          <DisplayEntryWrapper>
            <DisplayEntry>Area:</DisplayEntry>
            <DisplayEntry>{area}</DisplayEntry>
          </DisplayEntryWrapper>
        )}
      </>
    )}
  </DisplayWrapper>
);
DxfDetailsOverlay.displayName = 'DxfDetailsOverlay';
